import { useCallback, useMemo, useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import type { RowSelectionState } from "@tanstack/react-table";
import { m } from "#/paraglide/messages";
import { toISODateString } from "#/utils/date";
import { ListingFpPage } from "#/components/pages/listing-fp/listing-fp-page";
import { ScanCard } from "#/components/organisms/listing-fp/scan-card";
import { useScanMode } from "#/components/organisms/listing-fp/use-scan-mode";
import { useMarket } from "#/contexts/MarketContext";
import {
  useListingFp,
  useTransitionListingFp,
  type TransitionTargetStatus,
} from "#/lib/api/listing-fp";
import type { ListingFpRow } from "#/components/organisms/listing-fp/types";

export const Route = createFileRoute("/$tenantCode/$market/listing-fp")({
  beforeLoad: () => ({ getTitle: () => m["resources.listingFp.name"]() }),
  component: ListingFpRoute,
});

function ListingFpRoute() {
  const market = useMarket();
  const [date, setDate] = useState<string>(() => toISODateString(new Date()));
  const [previousParcel, setPreviousParcel] = useState<boolean>(true);
  const { mode, setMode } = useScanMode();

  const { data, isLoading, isFetching } = useListingFp({
    market,
    date,
    previousParcel,
    mode,
  });

  const rows = useMemo<ListingFpRow[]>(() => data?.member ?? [], [data]);
  const conjRows = useMemo(
    () => rows.filter((row) => row.type === "conj"),
    [rows],
  );
  const adjRows = useMemo(
    () => rows.filter((row) => row.type === "adj"),
    [rows],
  );

  const [conjSelection, setConjSelection] = useState<RowSelectionState>({});
  const [adjSelection, setAdjSelection] = useState<RowSelectionState>({});

  const allSelectedRows = useMemo<ListingFpRow[]>(
    () => [
      ...conjRows.filter((row) => conjSelection[`${row.orderId}`] === true),
      ...adjRows.filter((row) => adjSelection[`${row.orderId}`] === true),
    ],
    [conjRows, adjRows, conjSelection, adjSelection],
  );

  const { handleTransition, isPending } = useTransitionListingFp();

  const submitTransition = useCallback(
    async (orderIds: number[], target: TransitionTargetStatus) => {
      if (orderIds.length === 0) {
        return;
      }
      try {
        await handleTransition({ orderIds, targetStatus: target });
        setConjSelection({});
        setAdjSelection({});
      } catch (error) {
        console.error("listing-fp transition failed", error);
      }
    },
    [handleTransition],
  );

  const handleToggleSingle = useCallback(
    (orderId: number, target: TransitionTargetStatus) => {
      void submitTransition([orderId], target);
    },
    [submitTransition],
  );

  const handleBatch = useCallback(
    (orderIds: number[], target: TransitionTargetStatus) => {
      void submitTransition(orderIds, target);
    },
    [submitTransition],
  );

  const handleSelectAllByStatus = useCallback(
    (status: number, checked: boolean) => {
      const updateSelection =
        (sourceRows: ListingFpRow[]) =>
        (prev: RowSelectionState): RowSelectionState => {
          const next = { ...prev };
          sourceRows.forEach((row) => {
            if (row.status !== status) {
              return;
            }
            const key = `${row.orderId}`;
            if (checked) {
              next[key] = true;
            } else {
              delete next[key];
            }
          });
          return next;
        };
      setConjSelection(updateSelection(conjRows));
      setAdjSelection(updateSelection(adjRows));
    },
    [conjRows, adjRows],
  );

  const selectedIdSet = useMemo(
    () => new Set(allSelectedRows.map((row) => row.orderId)),
    [allSelectedRows],
  );

  const isAllSelectedForStatus = useCallback(
    (status: number) => {
      let hasAny = false;
      for (const row of rows) {
        if (row.status !== status) continue;
        hasAny = true;
        if (!selectedIdSet.has(row.orderId)) return false;
      }
      return hasAny;
    },
    [rows, selectedIdSet],
  );

  return (
    <>
      <ListingFpPage
        conjRows={conjRows}
        adjRows={adjRows}
        isLoading={isLoading}
        isPending={isPending}
        date={date}
        previousParcel={previousParcel}
        mode={mode}
        onDateChange={setDate}
        onPreviousParcelChange={setPreviousParcel}
        onModeChange={setMode}
        onToggleSingle={handleToggleSingle}
        conjSelection={conjSelection}
        adjSelection={adjSelection}
        onConjSelectionChange={setConjSelection}
        onAdjSelectionChange={setAdjSelection}
      />
      <ScanCard
        mode={mode}
        isFetching={isFetching}
        batch={{
          selectedRows: allSelectedRows,
          disabled: isLoading || isPending,
          onBatch: handleBatch,
          onSelectAllByStatus: handleSelectAllByStatus,
          isAllSelectedForStatus,
        }}
      />
    </>
  );
}
